import type { ReactElement, ReactNode } from 'react';
import { CheckField, DecimalField, SelectField } from '@ff-it/form';
import type { ActivityType, ProductKind } from 'types';
import { applicationOptions } from 'options';
import { ActivityField } from 'components';
import { directionOptions } from 'modules/campaign/row/smart';
import { getUnitOptions } from 'core/types';
import type { ProductFormConfig } from './types';
import { Config, ConfigToggle } from './Config';

export type ProductKindFieldsProps = {
  type: ActivityType;
  kind: ProductKind;
  vendorId: number | null;
  config?: ProductFormConfig;
  /** Type-owned replacement for the standard configuration toggle. */
  configurationToggle?: ReactNode;
  /** Service-category products do not expose a fallback price on the route form. */
  showServiceGrossPrice?: boolean;
};

function MediaFields({ type, vendorId, config, configurationToggle }: ProductKindFieldsProps): ReactElement {
  const short = 'col-6';
  const toggle = config || configurationToggle ? 'col-6' : 'col-12';
  const wide = 'col-12';
  return (
    <>
      <div className="form-row">
        <SelectField name="unit" label="Unit" className={short} options={getUnitOptions('MEDIA', type)} simple />
        <DecimalField name="gross_price" label="Fallback price" help="Price of unit" className={short} precision={16} />
      </div>
      <div className="form-row">
        <ActivityField
          name="activities"
          label="Activities"
          className={wide}
          isMulti
          kind="MEDIA"
          help="Leave empty for any"
        />
        <CheckField
          variant="checkbox"
          name="disabled"
          fieldLabel="Disabled"
          className={toggle}
          help="Hide this product from product pickers"
        />
        {config ? <ConfigToggle config={config} className={toggle} /> : configurationToggle}
      </div>
      {config && <Config config={config} type={type} vendorId={vendorId} />}
    </>
  );
}

function ServiceFields({ type, showServiceGrossPrice }: ProductKindFieldsProps): ReactElement {
  const short = showServiceGrossPrice ? 'col-6' : 'col-12';
  return (
    <>
      <div className="form-row">
        <SelectField name="unit" label="Unit" className={short} options={getUnitOptions('SERVICE', type)} simple />
        {showServiceGrossPrice && (
          <DecimalField
            name="gross_price"
            label="Fallback price"
            help="Price of unit"
            className={short}
            precision={16}
          />
        )}
      </div>
      <div className="form-row">
        <ActivityField
          name="activities"
          label="Activities"
          className="col-12"
          isMulti
          kind="SERVICE"
          help="Leave empty for any"
        />
        <CheckField
          variant="checkbox"
          name="disabled"
          fieldLabel="Disabled"
          className="col-12"
          help="Hide this product from product pickers"
        />
      </div>
    </>
  );
}

function SmartFields(_: ProductKindFieldsProps): ReactElement {
  const short = 'col-12';
  return (
    <>
      <div className="form-row">
        <SelectField
          name="direction"
          label="Result direction"
          className={short}
          options={directionOptions}
          simple
          required
        />
        <SelectField
          name="application"
          label="Application"
          className={short}
          options={applicationOptions}
          simple
          required
        />
        <CheckField
          variant="checkbox"
          name="disabled"
          fieldLabel="Disabled"
          className="col-12"
          help="Hide this product from product pickers"
        />
      </div>
      <ActivityField
        name="activities"
        label="Activities"
        className="col-12"
        isMulti
        kind="SERVICE"
        help="Leave empty for any"
      />
    </>
  );
}

export function ProductKindFields(props: ProductKindFieldsProps): ReactElement {
  switch (props.kind) {
    case 'MEDIA':
      return <MediaFields {...props} />;
    case 'SERVICE':
      return <ServiceFields {...props} />;
    case 'SMART':
      return <SmartFields {...props} />;
  }
}
